import type { GetOffersCargosParams } from "@/entities/get-offers-cargos/model";
import type { OFFER_CARGO_PROCESS_RECEPTION_STATUS } from "@/features/offer-cargo-process-status/badge-status";
import { BaseButton } from "@/shared/ui/BaseButton";
import { DateRangePicker } from "@/shared/ui/DateRangePicker";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { type MouseEvent, useEffect } from "react";
import { useForm } from "react-hook-form";
import type { ShipperCargoListPageProps } from "../../model";
import { LocationSelect } from "./location-select";
import {
  OfferCargoSearchFormSchema,
  type OfferCargoSearchFormSchemaType,
} from "./schema";
import { SelectReceptionStatus } from "./select-reception-status";

export type SearchFormCardProps = {
  getDraftOffersCargosParams: ShipperCargoListPageProps["getDraftOffersCargosParams"];
  receptionStatusFilter: OFFER_CARGO_PROCESS_RECEPTION_STATUS | undefined;
  onGetOffersCargosParamsChange: (
    params: Pick<
      GetOffersCargosParams,
      | "loadingOnFrom"
      | "loadingOnTo"
      | "loadingPrefecture"
      | "loadingAddress"
      | "unloadingOnFrom"
      | "unloadingOnTo"
      | "unloadingPrefecture"
      | "unloadingAddress"
    >,
  ) => void;
  onGetOffersCargosParamsReset: () => void;
  onReceptionStatusFilterChange: (
    status: OFFER_CARGO_PROCESS_RECEPTION_STATUS | undefined,
  ) => void;
};

export const SearchFormCard = ({
  getDraftOffersCargosParams,
  receptionStatusFilter,
  onGetOffersCargosParamsChange,
  onGetOffersCargosParamsReset,
  onReceptionStatusFilterChange,
}: SearchFormCardProps) => {
  const { handleSubmit, setValue, watch, reset } =
    useForm<OfferCargoSearchFormSchemaType>({
      resolver: zodResolver(OfferCargoSearchFormSchema),
    });
  useEffect(() => {
    if (!getDraftOffersCargosParams) {
      return;
    }
    reset({
      ...getDraftOffersCargosParams,
      receptionStatus: receptionStatusFilter,
    });
  }, [getDraftOffersCargosParams, receptionStatusFilter, reset]);
  const currentValue = watch();
  const handleSearchClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleSubmit((data) => {
      const { receptionStatus, contractStatus, ...rest } = data;
      onGetOffersCargosParamsChange(rest);
      onReceptionStatusFilterChange(receptionStatus);
    })();
  };
  const handleResetClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onGetOffersCargosParamsReset();
    reset();
  };

  return (
    <Stack
      p={3}
      spacing={3}
      borderRadius={"18px"}
      boxShadow="0px 1px 10px 0px #0000001A"
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={3}>
          <DateRangePicker
            label="積日"
            startDate={currentValue.loadingOnFrom}
            endDate={currentValue.loadingOnTo}
            onStartDateChange={(date) => setValue("loadingOnFrom", date)}
            onEndDateChange={(date) => setValue("loadingOnTo", date)}
          />
          <LocationSelect
            label="積地"
            prefecture={currentValue.loadingPrefecture}
            city={currentValue.loadingAddress}
            onPrefectureChange={(prefecture) =>
              setValue("loadingPrefecture", prefecture)
            }
            onCityChange={(city) => setValue("loadingAddress", city)}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <DateRangePicker
            label="卸日"
            startDate={currentValue.unloadingOnFrom}
            endDate={currentValue.unloadingOnTo}
            onStartDateChange={(date) => setValue("unloadingOnFrom", date)}
            onEndDateChange={(date) => setValue("unloadingOnTo", date)}
          />
          <LocationSelect
            label="卸地"
            prefecture={currentValue.unloadingPrefecture}
            city={currentValue.unloadingAddress}
            onPrefectureChange={(prefecture) =>
              setValue("unloadingPrefecture", prefecture)
            }
            onCityChange={(city) => setValue("unloadingAddress", city)}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <BaseFormLabel label="受付状況">
            <SelectReceptionStatus
              value={currentValue.receptionStatus}
              onChange={(v) => {
                setValue("receptionStatus", v);
              }}
            />
          </BaseFormLabel>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3}>
        <BaseButton variant="contained" onClick={handleSearchClick}>
          絞り込む
        </BaseButton>
        <BaseButton variant="outlined" onClick={handleResetClick}>
          リセット
        </BaseButton>
      </Stack>
    </Stack>
  );
};
